import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Popconfirm, Space, Card, Row, Col } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { perfilService, usuarioService } from '../services/api';
import { formatCPF, onlyDigits } from '../utils/documento';
import ShortId from './common/ShortId';

const UsuariosList = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [perfis, setPerfis] = useState([]);
  const [loadingPerfis, setLoadingPerfis] = useState(false);
  const [filtros, setFiltros] = useState({
    email: '',
    nome: '',
    perfil: null,
  });
  const [form] = Form.useForm();

  const compareText = (a, b) =>
    (a || '').toString().localeCompare((b || '').toString(), 'pt-BR', { sensitivity: 'base' });

  useEffect(() => {
    loadUsuarios();
    loadPerfis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUsuarios = async () => {
    setLoading(true);
    try {
      const response = await usuarioService.list();
      setUsuarios(response.data);
    } catch (error) {
      message.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const loadPerfis = async () => {
    setLoadingPerfis(true);
    try {
      const response = await perfilService.list();
      setPerfis(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      message.error('Erro ao carregar perfis');
    } finally {
      setLoadingPerfis(false);
    }
  };

  const showModal = (usuario = null) => {
    if (usuario) {
      setEditingId(usuario.id);
      form.setFieldsValue({
        ...usuario,
        id_perfil: usuario?.id_perfil || null,
      });
    } else {
      setEditingId(null);
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        id_perfil: values?.id_perfil ? String(values.id_perfil) : null,
      };

      if (editingId) {
        await usuarioService.update(editingId, payload);
        message.success('Usuário atualizado com sucesso');
      } else {
        // Para criar, precisa ter senha e confirmar_senha
        if (!values.senha || values.senha.length < 8) {
          message.error('Senha deve ter no mínimo 8 caracteres');
          return;
        }
        await usuarioService.create({
          ...payload,
          confirmar_senha: values.senha
        });
        message.success('Usuário criado com sucesso');
      }
      setIsModalVisible(false);
      loadUsuarios();
    } catch (error) {
      const errorMsg = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail))
        : 'Erro ao salvar usuário';
      message.error(errorMsg);
    }
  };

  const handleDelete = async (id) => {
    try {
      await usuarioService.delete(id);
      message.success('Usuário deletado com sucesso');
      loadUsuarios();
    } catch (error) {
      message.error('Erro ao deletar usuário');
    }
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltros((prev) => ({ ...prev, [campo]: valor }));
  };

  const limparFiltros = () => {
    setFiltros({ email: '', nome: '', perfil: null });
  };

  const usuariosFiltrados = usuarios.filter((usuario) => {
    const emailOk = (usuario?.email || '').toLowerCase().includes((filtros.email || '').toLowerCase().trim());
    const nomeOk = (usuario?.nome_completo || '').toLowerCase().includes((filtros.nome || '').toLowerCase().trim());
    const perfilOk = !filtros.perfil || String(usuario?.id_perfil || '') === String(filtros.perfil || '');
    return emailOk && nomeOk && perfilOk;
  });

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id) => <ShortId id={id} />,
      sorter: (a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => compareText(a.email, b.email),
    },
    {
      title: 'Nome Completo',
      dataIndex: 'nome_completo',
      key: 'nome_completo',
      sorter: (a, b) => compareText(a.nome_completo, b.nome_completo),
    },
    {
      title: 'CPF',
      dataIndex: 'cpf',
      key: 'cpf',
      render: (cpf) => formatCPF(cpf),
    },
    {
      title: 'Telefone',
      dataIndex: 'telefone',
      key: 'telefone',
      render: (telefone) => telefone || '-',
    },
    {
      title: 'Perfil',
      dataIndex: 'role',
      key: 'role',
      render: (role) => {
        const roleColors = {
          ADMIN: 'red',
          RH: 'blue',
          OFFICE: 'green',
          RESPONSAVEL: 'orange',
          PROFESSOR: 'purple',
          ALUNO: 'gray',
        };
        return <span style={{ color: roleColors[role] || 'var(--text-primary)' }}>{role}</span>;
      },
    },
    {
      title: 'Ativo',
      dataIndex: 'ativo',
      key: 'ativo',
      render: (ativo) => (ativo ? '✅ Sim' : '❌ Não'),
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => showModal(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="Deletar usuário"
            description="Tem certeza que deseja deletar este usuário?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sim"
            cancelText="Não"
          >
            <Button type="primary" danger size="small" icon={<DeleteOutlined />}>
              Deletar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <h2>Gerenciamento de Usuários</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
          Novo Usuário
        </Button>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} md={8}>
            <Input
              placeholder="Filtrar por e-mail"
              value={filtros.email}
              onChange={(e) => handleFiltroChange('email', e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} md={8}>
            <Input
              placeholder="Filtrar por nome"
              value={filtros.nome}
              onChange={(e) => handleFiltroChange('nome', e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} md={6}>
            <Select
              placeholder="Filtrar por perfil"
              value={filtros.perfil}
              onChange={(value) => handleFiltroChange('perfil', value)}
              allowClear
              loading={loadingPerfis}
              style={{ width: '100%' }}
              options={perfis.map((perfil) => ({
                value: String(perfil.id),
                label: perfil.nome,
              }))}
            />
          </Col>
          <Col xs={24} md={2}>
            <Button onClick={limparFiltros} block>
              Limpar
            </Button>
          </Col>
        </Row>
      </Card>

      <Table
        columns={columns}
        dataSource={usuariosFiltrados}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingId ? 'Editar Usuário' : 'Novo Usuário'}
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={() => setIsModalVisible(false)}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Email"
            name="email"
            rules={[{ required: true, type: 'email' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Nome Completo"
            name="nome_completo"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Perfil"
            name="id_perfil"
            rules={[{ required: true }]}
          >
            <Select
              placeholder="Selecione o perfil"
              loading={loadingPerfis}
              options={perfis.map((perfil) => ({
                value: String(perfil.id),
                label: perfil.nome,
              }))}
            >
            </Select>
          </Form.Item>
          <Form.Item
            label="CPF"
            name="cpf"
            normalize={(value) => onlyDigits(value || '').slice(0, 11)}
            getValueProps={(value) => ({ value: formatCPF(value || '') })}
            rules={[{ len: 11, message: 'CPF deve ter 11 dígitos' }]}
          >
            <Input placeholder="000.000.000-00" />
          </Form.Item>
          <Form.Item
            label="Telefone"
            name="telefone"
          >
            <Input placeholder="(00) 00000-0000" />
          </Form.Item>
          <Form.Item
            label="Ativo"
            name="ativo"
            valuePropName="checked"
          >
            <Select>
              <Select.Option value={true}>Sim</Select.Option>
              <Select.Option value={false}>Não</Select.Option>
            </Select>
          </Form.Item>
          {!editingId && (
            <Form.Item
              label="Senha"
              name="senha"
              rules={[{ required: true, min: 8 }]}
            >
              <Input.Password />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default UsuariosList;
